<template>
    <component :is="Loading" v-if="hasOnlyExternal || duringSubmit" />
    <component :is="Component" v-else v-bind="forwardedProps" />
</template>

<script lang="ts">
    import { Component, Prop, Watch, Vue } from 'vue-property-decorator';

    import type { IamClient } from '@evidentid/iam-client';

    import { useUserStore } from '../store/user';
    import { UserState } from '../types';

    @Component
    export default class AuthLoginWrapper extends Vue {
        @Prop()
        private Component!: Vue;

        @Prop()
        private Loading!: Vue;

        private redirectedAlready: boolean = false;

        // PRODUCT-26110 Task 10 (INSTANCE_LISTENERS): Vue's template compiler rejects
        // two bare `v-bind` directives on the same element ("Duplicate attribute"), so
        // `v-bind="$props"` and the former `v-on="$listeners"` (now `$attrs`, which
        // carries listeners as `onXxx` keys) must be merged into a single object.
        private get forwardedProps(): Record<string, unknown> {
            return { ...this.$props, ...this.$attrs };
        }

        private get iam(): IamClient {
            return (this as any).$auth.client;
        }

        private get state(): UserState {
            return useUserStore();
        }

        private get duringSubmit(): boolean {
            const { state } = this;
            return Boolean(!state.initialized || state.duringRegularLogin || state.duringSsoLogin);
        }

        private get hasOnlyExternal(): boolean {
            const availableMethods = this.iam.getAvailableLoginMethods();
            const hasOneMethodOnly = availableMethods.length === 1;
            const hasExternalMethod = availableMethods.includes('external');
            return hasExternalMethod && hasOneMethodOnly;
        }

        @Watch('duringSubmit', { immediate: true })
        private async redirect(): Promise<void> {
            if (!this.redirectedAlready && !this.duringSubmit && !this.state.data && this.hasOnlyExternal) {
                this.redirectedAlready = true;
                await useUserStore().logInWithSso('external');
            }
        }
    }
</script>
